import settingsService from './settingsService.js'
import { RealAIService } from './realAiService.js'
import { JSONValidator } from '../utils/jsonValidator.js'
import { AIResponseDiagnostic } from '../utils/aiResponseDiagnostic.js'
import { AIPostProcessor } from './aiPostProcessor.js'
import { InteractiveStoryPrompt } from './InteractiveStoryPrompt.js'

/**
 * 故事数据JSON Schema验证器
 */
class StoryDataValidator {
  static validateStoryData(data) {
    const errors = []
    
    // 验证基本结构
    if (!data || typeof data !== 'object') {
      errors.push('数据必须是对象')
      return { isValid: false, errors }
    }
    
    // 验证outline
    if (!data.outline || typeof data.outline !== 'object') {
      errors.push('缺少outline字段')
    } else {
      if (!data.outline.title || typeof data.outline.title !== 'string') {
        errors.push('outline.title必须是字符串')
      }
      if (!data.outline.summary || typeof data.outline.summary !== 'string') {
        errors.push('outline.summary必须是字符串')
      }
      if (!data.outline.mainPlot || typeof data.outline.mainPlot !== 'string') {
        errors.push('outline.mainPlot必须是字符串')
      }
      if (!data.outline.mainConflict || typeof data.outline.mainConflict !== 'string') {
        errors.push('outline.mainConflict必须是字符串')
      }
    }
    
    // 验证characters
    if (!Array.isArray(data.characters)) {
      errors.push('characters必须是数组')
    } else if (data.characters.length === 0) {
      errors.push('characters数组不能为空')
    } else {
      data.characters.forEach((char, index) => {
        if (!char.id || typeof char.id !== 'string') {
          errors.push(`characters[${index}].id必须是字符串`)
        }
        if (!char.name || typeof char.name !== 'string') {
          errors.push(`characters[${index}].name必须是字符串`)
        }
        if (!char.role || typeof char.role !== 'string') {
          errors.push(`characters[${index}].role必须是字符串`)
        }
        if (!char.description || typeof char.description !== 'string') {
          errors.push(`characters[${index}].description必须是字符串`)
        }
        if (!char.appearance || typeof char.appearance !== 'string') {
          errors.push(`characters[${index}].appearance必须是字符串`)
        }
        if (!char.background || typeof char.background !== 'string') {
          errors.push(`characters[${index}].background必须是字符串`)
        }
        if (!char.personality || typeof char.personality !== 'string') {
          errors.push(`characters[${index}].personality必须是字符串`)
        }
        if (!char.imagePrompt || typeof char.imagePrompt !== 'string') {
          errors.push(`characters[${index}].imagePrompt必须是字符串`)
        }
        if (!char.languageStyle || typeof char.languageStyle !== 'string') {
          errors.push(`characters[${index}].languageStyle必须是字符串`)
        }
      })
    }
    
    // 验证chapters
    if (!Array.isArray(data.chapters)) {
      errors.push('chapters必须是数组')
    } else if (data.chapters.length === 0) {
      errors.push('chapters数组不能为空')
    } else {
      data.chapters.forEach((chapter, chapterIndex) => {
        if (!chapter.id || typeof chapter.id !== 'number') {
          errors.push(`chapters[${chapterIndex}].id必须是数字`)
        }
        if (!chapter.title || typeof chapter.title !== 'string') {
          errors.push(`chapters[${chapterIndex}].title必须是字符串`)
        }
        if (!chapter.description || typeof chapter.description !== 'string') {
          errors.push(`chapters[${chapterIndex}].description必须是字符串`)
        }
        if (!chapter.status || typeof chapter.status !== 'string') {
          errors.push(`chapters[${chapterIndex}].status必须是字符串`)
        }
        if (!Array.isArray(chapter.scenes)) {
          errors.push(`chapters[${chapterIndex}].scenes必须是数组`)
        } else if (chapter.scenes.length === 0) {
          errors.push(`chapters[${chapterIndex}].scenes数组不能为空`)
        } else {
          chapter.scenes.forEach((scene, sceneIndex) => {
            if (!scene.id || typeof scene.id !== 'string') {
              errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].id必须是字符串`)
            }
            if (!scene.title || typeof scene.title !== 'string') {
              errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].title必须是字符串`)
            }
            if (!scene.description || typeof scene.description !== 'string') {
              errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].description必须是字符串`)
            }
            if (!scene.content || typeof scene.content !== 'string') {
              errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].content必须是字符串`)
            }
            if (!scene.imagePrompt || typeof scene.imagePrompt !== 'string') {
              errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].imagePrompt必须是字符串`)
            }
            if (typeof scene.hasChoices !== 'boolean') {
              errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].hasChoices必须是布尔值`)
            }
            if (!Array.isArray(scene.choices)) {
              errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].choices必须是数组`)
            }
            if (!Array.isArray(scene.dialogues)) {
              errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].dialogues必须是数组`)
            }
            if (!scene.background || typeof scene.background !== 'string') {
              errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].background必须是字符串`)
            }
            if (!scene.mood || typeof scene.mood !== 'string') {
              errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].mood必须是字符串`)
            }
            if (!scene.timeOfDay || typeof scene.timeOfDay !== 'string') {
              errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].timeOfDay必须是字符串`)
            }
            if (!scene.location || typeof scene.location !== 'string') {
              errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].location必须是字符串`)
            }
          })
        }
        if (!Array.isArray(chapter.endings)) {
          errors.push(`chapters[${chapterIndex}].endings必须是数组`)
        }
      })
    }
    
    return {
      isValid: errors.length === 0,
      errors
    }
  }
}

/**
 * 单次API调用AI服务
 * 一次性生成完整的故事结构，减少API调用失败
 */
export class SingleCallAIService {
  
  /**
   * 分阶段生成完整故事 (主入口)
   */
  static async generateCompleteStory(content, genre, style) {
    console.log('🎬 开始分阶段AI故事生成...')
    
    // 检查API配置
    const languageConfig = settingsService.getLanguageModelConfig()
    
    if (!languageConfig.apiKey || !languageConfig.apiUrl) {
      throw new Error('API未配置，请先配置API密钥和地址')
    }
    
    try {
      // 优化策略：使用单次API调用而不是分阶段，提高速度
      console.log('🚀 使用单次API调用策略，提高生成速度...')
      return await this.generateStoryWithSingleAPI(content, genre, style)
    } catch (error) {
      console.error('❌ 分阶段生成失败:', error)
      console.log('🔄 回退到单次调用模式...')
      
      // 如果分阶段失败，回退到原来的单次调用
      try {
        const result = await this.generateStoryWithSingleAPI(content, genre, style)
        console.log('✅ 回退模式成功，故事标题:', result.title)
        return result
      } catch (fallbackError) {
        console.error('❌ 回退模式也失败:', fallbackError)
        throw new Error(`故事生成失败: ${fallbackError.message}`)
      }
    }
  }

  /**
   * 使用单次API调用生成完整故事
   */
  static async generateStoryWithSingleAPI(content, genre, style) {
    console.log('🤖 使用单次API调用生成完整故事...')
    
    // 使用专业的互动故事创作提示词
    console.log('🎯 使用专业互动故事提示词生成...')
    const prompt = InteractiveStoryPrompt.generatePrompt(content, genre, style)

    const response = await RealAIService.callLanguageModel(prompt, {
      temperature: 0.8,
      maxTokens: 8192,   // 设置为API允许的最大值
      stream: false      // 保持非流式响应确保稳定性
    })

    console.log('📥 收到API响应，长度:', response.length)
    
    try {
      // 使用AI后处理模块处理响应
      console.log('🔄 使用AI后处理模块处理响应...')
      const processResult = await AIPostProcessor.processAIOutput(response, genre, style)
      
      if (processResult.success) {
        console.log('✅ AI后处理成功')
        console.log('📊 处理方法:', processResult.method)
        console.log('⏱️ 处理耗时:', processResult.processingTime + 'ms')
        
        if (processResult.warnings.length > 0) {
          console.log('⚠️ 处理警告:', processResult.warnings)
        }
        
        console.log('🔍 processResult.data 检查:', {
          hasData: !!processResult.data,
          dataType: typeof processResult.data,
          dataKeys: processResult.data ? Object.keys(processResult.data) : []
        })
        
        // 格式化故事数据
        console.log('🚀 开始格式化故事数据...')
        const formattedData = this.formatStoryData(processResult.data, genre, style)
        console.log('✅ 故事数据格式化完成，返回结果:', {
          hasTitle: !!formattedData?.title,
          title: formattedData?.title,
          hasChapters: !!formattedData?.chapters,
          chaptersCount: formattedData?.chapters?.length,
          hasCharacters: !!formattedData?.characters,
          charactersCount: formattedData?.characters?.length
        })
        
        return formattedData
      } else {
        console.error('❌ AI后处理失败')
        console.log('📊 处理报告:', AIPostProcessor.generateProcessingReport(processResult))
        
        // 进行详细的响应诊断
        const diagnostic = AIResponseDiagnostic.diagnoseResponse(response)
        console.log('🔍 详细诊断报告:', AIResponseDiagnostic.generateReport(diagnostic))
        
        throw new Error(`AI后处理失败: ${processResult.errors.join(', ')}`)
      }
    } catch (error) {
      console.error('❌ 故事生成失败:', error.message)
      console.log('📄 原始响应前500字符:', response.substring(0, 500))
      console.log('🔍 错误详情:', {
        errorType: error.constructor.name,
        message: error.message,
        stack: error.stack?.split('\n').slice(0, 3).join('\n')
      })
      
      throw error
    }
  }

  /**
   * 修复JSON错误
   */
  static fixJsonErrors(jsonString, genre = '冒险', style = '现代') {
    try {
      // 尝试直接解析
      JSON.parse(jsonString)
      return jsonString
    } catch (error) {
      console.log('🔧 尝试修复JSON错误...')
      console.log('🔍 原始JSON前200字符:', jsonString.substring(0, 200))
      
      let fixedJson = jsonString
      
      // 1. 清理JSON字符串
      // 移除可能的前后空白字符
      fixedJson = fixedJson.trim()
      
      // 移除可能的Markdown代码块标记
      fixedJson = fixedJson.replace(/^```json\s*/, '').replace(/\s*```$/, '')
      
      // 2. 修复中文字符导致的JSON错误
      // 修复未转义的中文引号
      fixedJson = fixedJson.replace(/([^\\])"([^"]*[\u4e00-\u9fff][^"]*)"([^:,}\]])/g, '$1\\"$2\\"$3')
      
      // 3. 修复常见的JSON错误
      // 移除尾随逗号
      fixedJson = fixedJson.replace(/,(\s*[}\]])/g, '$1')
      
      // 修复单引号为双引号（但要小心不要破坏已经正确的双引号）
      fixedJson = fixedJson.replace(/'([^']*)':/g, '"$1":') // 属性名
      fixedJson = fixedJson.replace(/: '([^']*)'/g, ': "$1"') // 属性值
      
      // 修复缺少引号的属性名
      fixedJson = fixedJson.replace(/(\w+):/g, '"$1":')
      
      // 4. 修复换行符和特殊字符
      // 只在字符串值内部转义换行符
      fixedJson = fixedJson.replace(/"([^"]*)\n([^"]*)"/g, '"$1\\n$2"')
      fixedJson = fixedJson.replace(/"([^"]*)\r([^"]*)"/g, '"$1\\r$2"')
      fixedJson = fixedJson.replace(/"([^"]*)\t([^"]*)"/g, '"$1\\t$2"')
      
      // 5. 修复多余的逗号
      fixedJson = fixedJson.replace(/,(\s*[}\]])/g, '$1')
      
      // 6. 确保JSON完整性
      // 检查大括号是否匹配
      const openBraces = (fixedJson.match(/\{/g) || []).length
      const closeBraces = (fixedJson.match(/\}/g) || []).length
      
      if (openBraces > closeBraces) {
        fixedJson += '}'.repeat(openBraces - closeBraces)
      }
      
      console.log('🔧 修复后的JSON前200字符:', fixedJson.substring(0, 200))
      
      try {
        const parsedJson = JSON.parse(fixedJson)
        console.log('✅ JSON修复成功')
        
        // 确保修复后的JSON包含必要字段
        const enhancedJson = this.enhanceJsonStructure(parsedJson, genre, style)
        return JSON.stringify(enhancedJson)
      } catch (secondError) {
        console.error('❌ JSON修复失败，无法解析API响应')
        console.log('🔍 修复失败的错误:', secondError.message)
        console.log('🔍 修复后的JSON前500字符:', fixedJson.substring(0, 500))
        throw new Error(`JSON修复失败: ${secondError.message}`)
      }
    }
  }

  /**
   * 增强JSON结构，确保包含所有必要字段
   */
  static enhanceJsonStructure(jsonData, genre = '冒险', style = '现代') {
    console.log('🔧 增强JSON结构...')
    
    // 严格验证基本结构
    if (!jsonData || typeof jsonData !== 'object') {
      throw new Error('JSON数据格式错误：数据必须是对象')
    }
    
    // 确保基本结构存在
    const enhanced = {
      outline: jsonData.outline || {
        title: "精彩故事",
        summary: "这是一个精彩的故事",
        mainPlot: "主角将面临各种挑战和选择",
        mainConflict: "主要冲突"
      },
      characters: Array.isArray(jsonData.characters) ? jsonData.characters : [],
      chapters: Array.isArray(jsonData.chapters) ? jsonData.chapters : [],
      branchPoints: Array.isArray(jsonData.branchPoints) ? jsonData.branchPoints : [],
      finalEndings: Array.isArray(jsonData.finalEndings) ? jsonData.finalEndings : [],
      genre: jsonData.genre || genre,
      style: jsonData.style || style
    }
    
    // 验证outline结构
    if (!enhanced.outline.title || typeof enhanced.outline.title !== 'string') {
      throw new Error('JSON结构错误：outline.title必须是字符串')
    }
    if (!enhanced.outline.summary || typeof enhanced.outline.summary !== 'string') {
      throw new Error('JSON结构错误：outline.summary必须是字符串')
    }
    if (!enhanced.outline.mainPlot || typeof enhanced.outline.mainPlot !== 'string') {
      throw new Error('JSON结构错误：outline.mainPlot必须是字符串')
    }
    if (!enhanced.outline.mainConflict || typeof enhanced.outline.mainConflict !== 'string') {
      throw new Error('JSON结构错误：outline.mainConflict必须是字符串')
    }
    
    // 验证characters结构
    if (enhanced.characters.length === 0) {
      throw new Error('JSON结构错误：characters数组不能为空')
    }
    
    // 验证每个角色的必要字段
    enhanced.characters.forEach((char, index) => {
      if (!char.id || typeof char.id !== 'string') {
        throw new Error(`JSON结构错误：characters[${index}].id必须是字符串`)
      }
      if (!char.name || typeof char.name !== 'string') {
        throw new Error(`JSON结构错误：characters[${index}].name必须是字符串`)
      }
      if (!char.role || typeof char.role !== 'string') {
        throw new Error(`JSON结构错误：characters[${index}].role必须是字符串`)
      }
      if (!char.description || typeof char.description !== 'string') {
        throw new Error(`JSON结构错误：characters[${index}].description必须是字符串`)
      }
      if (!char.appearance || typeof char.appearance !== 'string') {
        throw new Error(`JSON结构错误：characters[${index}].appearance必须是字符串`)
      }
      if (!char.background || typeof char.background !== 'string') {
        throw new Error(`JSON结构错误：characters[${index}].background必须是字符串`)
      }
      if (!char.personality || typeof char.personality !== 'string') {
        throw new Error(`JSON结构错误：characters[${index}].personality必须是字符串`)
      }
      if (!char.imagePrompt || typeof char.imagePrompt !== 'string') {
        throw new Error(`JSON结构错误：characters[${index}].imagePrompt必须是字符串`)
      }
      if (!char.languageStyle || typeof char.languageStyle !== 'string') {
        throw new Error(`JSON结构错误：characters[${index}].languageStyle必须是字符串`)
      }
    })
    
    // 验证chapters结构
    if (enhanced.chapters.length === 0) {
      throw new Error('JSON结构错误：chapters数组不能为空')
    }
    
    // 验证每个章节的必要字段
    enhanced.chapters.forEach((chapter, chapterIndex) => {
      if (!chapter.id || typeof chapter.id !== 'number') {
        throw new Error(`JSON结构错误：chapters[${chapterIndex}].id必须是数字`)
      }
      if (!chapter.title || typeof chapter.title !== 'string') {
        throw new Error(`JSON结构错误：chapters[${chapterIndex}].title必须是字符串`)
      }
      if (!chapter.description || typeof chapter.description !== 'string') {
        throw new Error(`JSON结构错误：chapters[${chapterIndex}].description必须是字符串`)
      }
      if (!chapter.status || typeof chapter.status !== 'string') {
        throw new Error(`JSON结构错误：chapters[${chapterIndex}].status必须是字符串`)
      }
      if (!Array.isArray(chapter.scenes)) {
        throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes必须是数组`)
      }
      if (chapter.scenes.length === 0) {
        throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes数组不能为空`)
      }
      if (!Array.isArray(chapter.endings)) {
        throw new Error(`JSON结构错误：chapters[${chapterIndex}].endings必须是数组`)
      }
      
      // 验证每个场景的必要字段
      chapter.scenes.forEach((scene, sceneIndex) => {
        if (!scene.id || typeof scene.id !== 'string') {
          throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes[${sceneIndex}].id必须是字符串`)
        }
        if (!scene.title || typeof scene.title !== 'string') {
          throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes[${sceneIndex}].title必须是字符串`)
        }
        if (!scene.description || typeof scene.description !== 'string') {
          throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes[${sceneIndex}].description必须是字符串`)
        }
        if (!scene.content || typeof scene.content !== 'string') {
          throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes[${sceneIndex}].content必须是字符串`)
        }
        if (!scene.imagePrompt || typeof scene.imagePrompt !== 'string') {
          throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes[${sceneIndex}].imagePrompt必须是字符串`)
        }
        if (typeof scene.hasChoices !== 'boolean') {
          throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes[${sceneIndex}].hasChoices必须是布尔值`)
        }
        if (!Array.isArray(scene.choices)) {
          throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes[${sceneIndex}].choices必须是数组`)
        }
        if (!Array.isArray(scene.dialogues)) {
          throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes[${sceneIndex}].dialogues必须是数组`)
        }
        if (!scene.background || typeof scene.background !== 'string') {
          throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes[${sceneIndex}].background必须是字符串`)
        }
        if (!scene.mood || typeof scene.mood !== 'string') {
          throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes[${sceneIndex}].mood必须是字符串`)
        }
        if (!scene.timeOfDay || typeof scene.timeOfDay !== 'string') {
          throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes[${sceneIndex}].timeOfDay必须是字符串`)
        }
        if (!scene.location || typeof scene.location !== 'string') {
          throw new Error(`JSON结构错误：chapters[${chapterIndex}].scenes[${sceneIndex}].location必须是字符串`)
        }
      })
    })
    
    console.log('✅ JSON结构增强完成，所有验证通过')
    return enhanced
  }


  /**
   * 格式化故事数据
   */
  static formatStoryData(storyData, genre = '冒险', style = '现代') {
    console.log('🔄 格式化故事数据...')
    
    // 确保storyData有必要的结构
    if (!storyData) {
      console.error('❌ storyData为空，无法格式化')
      throw new Error('storyData为空')
    }
    
    console.log('📊 storyData结构检查:', {
      hasOutline: !!storyData.outline,
      hasCharacters: Array.isArray(storyData.characters),
      charactersCount: storyData.characters?.length || 0,
      hasChapters: Array.isArray(storyData.chapters),
      chaptersCount: storyData.chapters?.length || 0,
      hasGenre: !!storyData.genre,
      hasStyle: !!storyData.style
    })
    
    // 从storyData中提取genre和style，如果存在的话
    const finalGenre = storyData.genre || genre
    const finalStyle = storyData.style || style
    
    console.log('📝 使用参数:', { genre: finalGenre, style: finalStyle })
    
    // 确保characters数组存在
    if (!storyData.characters || !Array.isArray(storyData.characters)) {
      console.warn('⚠️ characters数组不存在或格式错误，使用默认值')
      storyData.characters = []
    }
    
    // 格式化角色数据
    const formattedCharacters = storyData.characters.map((char, index) => ({
      id: char.id || `char-${index + 1}`,
      name: char.name || `角色${index + 1}`,
      role: char.role || 'supporting',
      description: char.description || '',
      appearance: char.appearance || '神秘而引人注目',
      background: char.background || '与主角命运交织的角色',
      personality: char.personality || '复杂而多面',
      imagePrompt: char.imagePrompt || 'character design',
      languageStyle: char.languageStyle || '普通',
      isActive: true,
      firstAppearance: '故事开始',
      lastAppearance: '故事结束',
      dialogueCount: 0,
      stats: {
        intelligence: 70 + Math.floor(Math.random() * 20),
        strength: 60 + Math.floor(Math.random() * 20),
        charisma: 70 + Math.floor(Math.random() * 20),
        wisdom: 65 + Math.floor(Math.random() * 20)
      },
      imageUrl: `https://picsum.photos/200/200?random=${10 + index}`
    }))

    // 确保chapters数组存在
    if (!storyData.chapters || !Array.isArray(storyData.chapters)) {
      console.warn('⚠️ chapters数组不存在或格式错误，使用默认值')
      storyData.chapters = []
    }
    
    // 格式化章节数据
    const formattedChapters = storyData.chapters.map(chapter => ({
      ...chapter,
      scenes: (chapter.scenes || []).map(scene => ({
        ...scene,
        imageUrl: `https://picsum.photos/800/600?random=${Math.floor(Math.random() * 1000)}`,
        storyBeat: scene.storyBeat || '故事发展的重要节点'
      }))
    }))

    // 构建连接关系
    const connections = this.buildConnections(formattedChapters)

    const completeStory = {
      id: `story-${Date.now()}`,
      title: storyData.outline?.title || storyData.title || '精彩故事',
      summary: storyData.outline?.summary || storyData.summary || '这是一个精彩的故事',
      mainPlot: storyData.outline?.mainPlot || storyData.mainPlot || '主角将面临各种挑战和选择',
      genre: finalGenre,
      style: finalStyle,
      status: 'completed',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      
      // 保留原始大纲数据
      outline: storyData.outline || {
        title: storyData.title || '精彩故事',
        summary: storyData.summary || '这是一个精彩的故事',
        mainPlot: storyData.mainPlot || '主角将面临各种挑战和选择'
      },
      
      // 章节数据
      chapters: formattedChapters,
      
      // 角色数据
      characters: formattedCharacters,
      
      // 场景数据（扁平化）
      scenes: formattedChapters.flatMap(ch => ch.scenes),
      
      // 连接关系
      connections: connections,
      
      // 分支点和结局
      branchPoints: storyData.branchPoints || [],
      endings: storyData.finalEndings || [],
      
      // 统计信息
      stats: {
        totalChapters: formattedChapters.length,
        totalScenes: formattedChapters.reduce((sum, ch) => sum + ch.scenes.length, 0),
        totalCharacters: formattedCharacters.length,
        totalConnections: connections.length,
        completedScenes: formattedChapters.reduce((sum, ch) => 
          sum + ch.scenes.filter(s => s.content && s.content.length > 100).length, 0
        )
      }
    }

    console.log('✅ 故事数据格式化完成:', {
      title: completeStory.title,
      chapters: completeStory.stats.totalChapters,
      scenes: completeStory.stats.totalScenes,
      characters: completeStory.stats.totalCharacters
    })

    return completeStory
  }

  /**
   * 构建连接关系
   */
  static buildConnections(chapters) {
    const connections = []
    
    for (const chapter of chapters) {
      for (let i = 0; i < chapter.scenes.length; i++) {
        const currentScene = chapter.scenes[i]
        
        // 添加场景间的线性连接
        if (i < chapter.scenes.length - 1) {
          const nextScene = chapter.scenes[i + 1]
          connections.push({
            id: `conn-${currentScene.id}-${nextScene.id}`,
            source: currentScene.id,
            target: nextScene.id,
            type: 'linear',
            label: '继续'
          })
        }
        
        // 添加分支选择的连接
        if (currentScene.choices && currentScene.choices.length > 0) {
          for (const choice of currentScene.choices) {
            if (choice.targetNodeId) {
              connections.push({
                id: `conn-${currentScene.id}-${choice.targetNodeId}`,
                source: currentScene.id,
                target: choice.targetNodeId,
                type: 'choice',
                label: choice.text,
                choiceId: choice.id
              })
            }
          }
        }
      }
    }
    
    return connections
  }

  /**
   * 第一阶段：生成故事大纲和角色设定
   */
  static async generateStoryOutline(content, genre, style) {
    console.log('📋 生成故事大纲和角色设定...')
    
    const outlinePrompt = `基于用户创意生成故事大纲：

创意：${content}
类型：${genre}
风格：${style}

输出JSON格式：
{
  "outline": {
    "title": "故事标题",
    "summary": "150字摘要",
    "mainPlot": "主线剧情",
    "mainConflict": "核心冲突"
  },
  "characters": [
    {
      "id": "char-001",
      "name": "角色名",
      "role": "protagonist|supporting|antagonist",
      "description": "角色描述",
      "appearance": "外观",
      "background": "背景",
      "personality": "性格",
      "imagePrompt": "英文生图提示词",
      "languageStyle": "说话风格"
    }
  ],
  "chapters": [
    {
      "id": 1,
      "title": "第一章",
      "description": "章节概述",
      "expectedScenes": 4
    }
  ]
}

要求：创建3-4个角色，确保JSON格式正确。`

    const response = await RealAIService.callLanguageModel(outlinePrompt, {
      temperature: 0.7,
      maxTokens: 8192,   // 设置为API允许的最大值
      stream: false     // 暂时禁用流式响应，确保稳定性
    })

    console.log('📥 收到大纲API响应，长度:', response.length)
    
    try {
      const processResult = await AIPostProcessor.processAIOutput(response, genre, style)
      
      if (processResult.success) {
        console.log('✅ 大纲处理成功')
        return processResult.data
      } else {
        throw new Error(`大纲处理失败: ${processResult.errors.join(', ')}`)
      }
    } catch (error) {
      console.error('❌ 大纲生成失败:', error.message)
      throw error
    }
  }

  /**
   * 第二阶段：生成详细的第一章内容
   */
  static async generateDetailedChapter(outline, originalContent, genre, style) {
    console.log('📚 生成详细的第一章内容...')
    
    const charactersInfo = outline.characters?.map(char => 
      `${char.name}(${char.role}): ${char.description}`
    ).join('\n') || '主要角色信息'
    
    const firstChapter = outline.chapters?.[0] || { title: '第一章', expectedScenes: 4 }
    
    const chapterPrompt = `基于故事大纲生成第一章详细内容：

故事：${outline.outline?.title || '互动故事'}
角色：${charactersInfo}

生成包含4个场景的第一章，JSON格式：
{
  "chapter": {
    "id": 1,
    "title": "第一章",
    "scenes": [
      {
        "id": "scene-1-1",
        "title": "场景标题",
        "description": "场景描述",
        "content": "场景内容(500-800字)",
        "chapter": 1,
        "scene": 1,
        "imagePrompt": "英文生图提示词",
        "dialogues": [
          {
            "character": "角色名",
            "content": "对话内容"
          }
        ],
        "choices": [
          {
            "id": "choice-1",
            "text": "选择文本",
            "targetNodeId": "scene-1-2",
            "nextScene": "scene-1-2"
          }
        ],
        "hasChoices": false,
        "background": "背景",
        "mood": "氛围",
        "timeOfDay": "时间",
        "location": "地点"
      }
    ]
  }
}

要求：
1. 4个场景，每个500-800字
2. 丰富的角色对话
3. 关键场景严格限制为2个选择分支
4. 确保JSON格式正确`

    const response = await RealAIService.callLanguageModel(chapterPrompt, {
      temperature: 0.8,
      maxTokens: 8192,   // 设置为API允许的最大值
      stream: false     // 暂时禁用流式响应，确保稳定性
    })

    console.log('📥 收到章节API响应，长度:', response.length)
    
    try {
      // 第二阶段专门处理章节数据，使用简化的解析流程
      console.log('🔧 专门解析章节数据结构...')
      console.log('📄 章节响应原始内容:')
      console.log('=' .repeat(50))
      console.log(response.substring(0, 500) + '...')
      console.log('=' .repeat(50))
      
      let chapterData = null
      
      // 快速JSON解析策略 - 减少处理步骤
      try {
        let jsonText = response.trim()
        
        // 快速提取JSON
        if (jsonText.includes('```json')) {
          const start = jsonText.indexOf('```json') + 7
          const end = jsonText.indexOf('```', start)
          jsonText = jsonText.substring(start, end)
        } else if (jsonText.includes('```')) {
          const start = jsonText.indexOf('```') + 3
          const end = jsonText.indexOf('```', start)
          jsonText = jsonText.substring(start, end)
        }
        
        // 查找JSON边界
        const firstBrace = jsonText.indexOf('{')
        const lastBrace = jsonText.lastIndexOf('}')
        
        if (firstBrace !== -1 && lastBrace !== -1) {
          jsonText = jsonText.substring(firstBrace, lastBrace + 1)
        }
        
        chapterData = JSON.parse(jsonText)
        console.log('✅ 快速JSON解析成功')
        
      } catch (parseError) {
        console.log('⚠️ 快速JSON解析失败，使用紧急提取...', parseError.message)
        
        // 仅在必要时使用紧急提取
        const { EmergencyJSONFixer } = await import('../utils/EmergencyJSONFixer.js')
        const emergencyResult = EmergencyJSONFixer.extractStoryData(response)
        
        if (emergencyResult.success) {
          chapterData = emergencyResult.data
          console.log('✅ 紧急提取成功')
        } else {
          throw new Error('所有JSON解析方法都失败')
        }
      }
      
      // 验证章节数据结构
      if (chapterData) {
        console.log('🔍 章节数据结构检查:', {
          hasData: !!chapterData,
          dataType: typeof chapterData,
          keys: chapterData ? Object.keys(chapterData) : [],
          hasChapter: !!chapterData.chapter,
          hasDirectScenes: !!chapterData.scenes,
          chapterKeys: chapterData.chapter ? Object.keys(chapterData.chapter) : [],
          hasScenes: !!chapterData.chapter?.scenes,
          scenesCount: chapterData.chapter?.scenes?.length || chapterData.scenes?.length || 0
        })
        
        return chapterData
      } else {
        throw new Error('解析后的章节数据为空')
      }
      
    } catch (error) {
      console.error('❌ 章节生成失败:', error.message)
      throw error
    }
  }

  /**
   * 智能提取场景数据
   */
  static extractScenes(chapterContent) {
    console.log('🔍 智能提取场景数据...')
    
    // 场景提取的优先级序列
    const extractionPaths = [
      () => chapterContent.chapter?.scenes,           // 标准：chapter.scenes
      () => chapterContent.scenes,                    // 直接：scenes
      () => chapterContent.chapters?.[0]?.scenes,     // 章节数组：chapters[0].scenes
      () => chapterContent.data?.chapter?.scenes,     // 嵌套：data.chapter.scenes
      () => chapterContent.data?.scenes,              // 嵌套：data.scenes
      () => {                                         // 特殊处理：如果chapter是单个对象但包含场景信息
        if (chapterContent.chapter && typeof chapterContent.chapter === 'object' && 
            !Array.isArray(chapterContent.chapter) && !chapterContent.chapter.scenes) {
          // 将整个chapter作为单个场景处理
          return [chapterContent.chapter]
        }
        return null
      }
    ]
    
    for (let i = 0; i < extractionPaths.length; i++) {
      try {
        const scenes = extractionPaths[i]()
        if (scenes && Array.isArray(scenes) && scenes.length > 0) {
          console.log(`✅ 场景提取成功 (路径${i+1})，场景数:`, scenes.length)
          console.log('🔍 场景预览:', scenes.map(s => ({ 
            id: s.id, 
            title: s.title, 
            hasDialogues: !!s.dialogues,
            hasChoices: !!s.choices,
            dialogueCount: s.dialogues?.length || 0,
            choiceCount: s.choices?.length || 0
          })))
          return scenes
        }
      } catch (error) {
        console.log(`⚠️ 路径${i+1}提取失败:`, error.message)
      }
    }
    
    console.log('❌ 所有场景提取路径都失败')
    return []
  }

  /**
   * 修复场景连接关系
   */
  static fixSceneConnections(scenes) {
    console.log('🔧 修复场景连接关系...')
    
    if (!scenes || scenes.length === 0) {
      return
    }
    
    // 首先修复场景的chapter和scene字段
    scenes.forEach((scene, index) => {
      if (!scene.chapter) {
        scene.chapter = 1
      }
      if (!scene.scene) {
        scene.scene = index + 1
      }
    })
    
    const sceneIds = scenes.map(s => s.id)
    let fixedCount = 0
    
    // 分析场景类型
    const sceneTypes = scenes.map((scene, index) => {
      const hasChoices = scene.choices && scene.choices.length > 0
      const isLinear = !hasChoices
      const isBranch = hasChoices && scene.choices.length >= 2
      const isDecision = hasChoices && scene.choices.length === 1
      
      return { scene, index, hasChoices, isLinear, isBranch, isDecision }
    })
    
    console.log('📊 场景类型分析:', sceneTypes.map(s => ({
      title: s.scene.title,
      type: s.isBranch ? '分支' : s.isDecision ? '决策' : s.isLinear ? '线性' : '未知',
      choices: s.scene.choices?.length || 0
    })))
    
    scenes.forEach((scene, index) => {
      if (scene.choices && scene.choices.length > 0) {
        scene.choices.forEach((choice, choiceIndex) => {
          if (!choice.nextScene || !sceneIds.includes(choice.nextScene)) {
            // 智能修复连接目标
            let targetId = null
            
            if (index < scenes.length - 1) {
              // 优先指向下一个场景
              targetId = scenes[index + 1].id
              console.log(`🔧 修复选择 ${choice.id}: 指向下一个场景 ${targetId}`)
            } else {
              // 最后一个场景，根据分支类型决定目标
              if (scene.choices.length > 1) {
                // 多分支场景，创建不同的结局
                targetId = `ending-${scene.id}-${choiceIndex + 1}`
                console.log(`🔧 修复选择 ${choice.id}: 创建结局场景 ${targetId}`)
              } else {
                // 单分支场景，指向第一个场景形成循环
                targetId = scenes[0].id
                console.log(`🔧 修复选择 ${choice.id}: 指向第一个场景形成循环 ${targetId}`)
              }
            }
            
            choice.nextScene = targetId
            choice.targetNodeId = targetId
            fixedCount++
          } else if (!choice.targetNodeId) {
            // 同步修复targetNodeId
            choice.targetNodeId = choice.nextScene
            fixedCount++
            console.log(`🔧 同步修复选择 ${choice.id}: targetNodeId = ${choice.nextScene}`)
          }
        })
      }
    })
    
    console.log(`✅ 场景连接修复完成，修复数: ${fixedCount}`)
  }

  /**
   * 生成场景连接关系
   */
  static generateConnections(scenes) {
    console.log('🔗 生成场景连接关系...')
    const connections = []
    
    if (!scenes || scenes.length === 0) {
      console.log('⚠️ 没有场景数据，跳过连接生成')
      return connections
    }
    
    scenes.forEach(scene => {
      if (scene.choices && scene.choices.length > 0) {
        scene.choices.forEach(choice => {
          const targetId = choice.targetNodeId || choice.nextScene
          if (targetId) {
            connections.push({
              id: `conn-${scene.id}-${choice.id}`,
              source: scene.id,
              target: targetId,
              choiceId: choice.id,
              choiceText: choice.text,
              type: choice.type || 'normal'
            })
          }
        })
      }
    })
    
    console.log(`✅ 生成连接关系完成，连接数: ${connections.length}`)
    return connections
  }

  /**
   * 生成分支点
   */
  static generateBranchPoints(scenes) {
    console.log('🌿 生成分支点...')
    const branchPoints = []
    
    if (!scenes || scenes.length === 0) {
      return branchPoints
    }
    
    scenes.forEach(scene => {
      if (scene.choices && scene.choices.length > 1) {
        branchPoints.push({
          id: `branch-${scene.id}`,
          sceneId: scene.id,
          sceneTitle: scene.title,
          choices: scene.choices.map(choice => ({
            id: choice.id,
            text: choice.text,
            targetNodeId: choice.targetNodeId || choice.nextScene,
            nextScene: choice.nextScene,
            type: choice.type || 'normal'
          }))
        })
      }
    })
    
    console.log(`✅ 生成分支点完成，分支数: ${branchPoints.length}`)
    return branchPoints
  }

  /**
   * 生成结局
   */
  static generateEndings(scenes) {
    console.log('🎯 生成结局...')
    const endings = []
    
    if (!scenes || scenes.length === 0) {
      return endings
    }
    
    // 查找没有出边的场景（结局场景）
    const sceneIds = new Set(scenes.map(s => s.id))
    const hasOutgoingConnections = new Set()
    
    scenes.forEach(scene => {
      if (scene.choices && scene.choices.length > 0) {
        scene.choices.forEach(choice => {
          if (choice.nextScene && sceneIds.has(choice.nextScene)) {
            hasOutgoingConnections.add(scene.id)
          }
        })
      }
    })
    
    // 为没有出边的场景创建结局对象
    scenes.forEach(scene => {
      if (!hasOutgoingConnections.has(scene.id)) {
        endings.push({
          id: `ending-${scene.id}`,
          sceneId: scene.id,
          title: `结局: ${scene.title}`,
          description: '故事结局',
          type: 'ending'
        })
      }
    })
    
    console.log(`✅ 生成结局完成，结局数: ${endings.length}`)
    return endings
  }

  /**
   * 合并故事大纲和章节内容
   */
  static mergeStoryParts(outline, chapterContent, genre, style) {
    console.log('🔀 合并故事各部分...')
    
    // 构建完整的故事结构
    const mergedStory = {
      // 基本信息
      id: `story-${Date.now()}`,
      title: outline.outline?.title || '互动故事',
      summary: outline.outline?.summary || '精彩的互动故事',
      mainPlot: outline.outline?.mainPlot || '',
      genre: genre,
      style: style,
      status: 'completed',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      
      // 故事大纲
      outline: outline.outline || {},
      
      // 角色信息
      characters: outline.characters || [],
      
      // 世界构建
      worldBuilding: outline.worldBuilding || {},
      
      // 章节内容 - 确保与编辑器兼容的格式
      chapters: chapterContent.chapter ? [chapterContent.chapter] : 
                chapterContent.chapters ? chapterContent.chapters : 
                outline.chapters || [],  // 使用大纲中的章节作为备选
      
      // 扁平化场景数据 - 智能提取第一章详细场景
      scenes: this.extractScenes(chapterContent) || [],
      
      // 生成连接关系 - 基于场景选择自动生成
      connections: [],
      branchPoints: [],
      endings: [],
      
      // 统计信息 - 使用实际的场景数据
      stats: {
        totalCharacters: outline.characters?.length || 0,
        totalChapters: 1,
        totalScenes: 0, // 将在下面计算
        totalChoices: 0,
        totalDialogues: 0,
        estimatedPlayTime: '15-20分钟'
      }
    }
    
    // 计算统计信息 - 使用实际的scenes数组
    if (mergedStory.scenes && mergedStory.scenes.length > 0) {
      mergedStory.stats.totalScenes = mergedStory.scenes.length
      mergedStory.stats.totalChoices = mergedStory.scenes.reduce((sum, scene) => 
        sum + (scene.choices?.length || 0), 0)
      mergedStory.stats.totalDialogues = mergedStory.scenes.reduce((sum, scene) => 
        sum + (scene.dialogues?.length || 0), 0)
      
      // 修复场景连接关系
      this.fixSceneConnections(mergedStory.scenes)
      
      // 基于场景生成连接关系
      mergedStory.connections = this.generateConnections(mergedStory.scenes)
      mergedStory.branchPoints = this.generateBranchPoints(mergedStory.scenes)
      mergedStory.endings = this.generateEndings(mergedStory.scenes)
    }
    
    console.log('✅ 故事合并完成:', {
      title: mergedStory.title,
      characters: mergedStory.stats.totalCharacters,
      scenes: mergedStory.stats.totalScenes,
      choices: mergedStory.stats.totalChoices,
      dialogues: mergedStory.stats.totalDialogues
    })
    
    return mergedStory
  }

}
